import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { categoryLabel } from "@/lib/categories";

const BASE = "https://sellavo.com";

export const Route = createFileRoute("/guider/$slug")({
  loader: async ({ params }) => {
    // Guidetexterna är tunga – laddas som eget paket bara för den här sidan.
    const { findGuide, relatedGuides } = await import("@/lib/guides");
    const guide = findGuide(params.slug);
    if (!guide) throw notFound();
    return { guide, others: relatedGuides(guide) };
  },
  head: ({ params, loaderData }) => {
    const guide = loaderData?.guide;
    if (!guide) {
      return {
        meta: [{ title: "Guiden hittades inte | Sellavo" }, { name: "robots", content: "noindex" }],
      };
    }
    const title = `${guide.metaTitle} | Sellavo`;
    const url = `${BASE}/guider/${params.slug}`;
    const scripts: { type: string; children: string }[] = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: guide.h1,
          description: guide.description,
          abstract: guide.summary,
          mainEntityOfPage: url,
          inLanguage: "sv-SE",
          dateModified: guide.updated,
          datePublished: guide.updated,
          author: { "@type": "Organization", name: "Sellavo", url: BASE },
          publisher: { "@type": "Organization", name: "Sellavo", url: BASE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: guide.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Start", item: BASE },
            { "@type": "ListItem", position: 2, name: "Guider", item: `${BASE}/guider` },
            { "@type": "ListItem", position: 3, name: guide.title, item: url },
          ],
        }),
      },
    ];

    if (guide.steps?.length) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: guide.h1,
          description: guide.summary,
          inLanguage: "sv-SE",
          step: guide.steps.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.title,
            text: s.text,
          })),
        }),
      });
    }

    return {
      meta: [
        { title },
        { name: "description", content: guide.description },
        { property: "og:title", content: title },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "article:modified_time", content: guide.updated },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  component: GuidePage,
  errorComponent: ({ error }) => (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 lg:px-8" role="alert">
      <p className="text-muted-foreground">{error.message}</p>
    </main>
  ),
  notFoundComponent: GuideNotFound,
});

function GuideNotFound() {
  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
      <h1 className="font-heading text-2xl font-extrabold text-foreground">
        Guiden finns inte längre
      </h1>
      <p className="mt-3 text-muted-foreground">Här är alla guider vi har just nu.</p>
      <Button asChild className="mt-6">
        <Link to="/guider">Till guiderna</Link>
      </Button>
    </main>
  );
}

function GuidePage() {
  const { guide, others } = Route.useLoaderData();

  return (
    <main className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <nav aria-label="Brödsmulor" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Start
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/guider" className="hover:text-foreground">
          Guider
        </Link>
      </nav>

      <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl">
        {guide.h1}
      </h1>
      <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wide text-primary">
          <Clock className="h-3.5 w-3.5" />
          {guide.readMinutes} min läsning
        </span>
        <span>
          Uppdaterad{" "}
          <time dateTime={guide.updated}>
            {new Date(guide.updated).toLocaleDateString("sv-SE", {
              year: "numeric",
              month: "long",
            })}
          </time>
        </span>
      </p>

      <div className="mt-6 rounded-lg border border-primary/25 bg-primary/5 p-5">
        <p className="font-heading text-[11px] font-bold uppercase tracking-wide text-primary">
          Det korta svaret
        </p>
        <p className="mt-2 text-sm leading-relaxed text-foreground sm:text-base">{guide.summary}</p>
      </div>

      <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
        {guide.intro}
      </p>

      {guide.sections.length > 2 ? (
        <nav aria-label="Innehåll" className="mt-8 rounded-lg border border-border bg-card p-5">
          <p className="font-heading text-sm font-bold text-foreground">Innehåll</p>
          <ol className="mt-3 space-y-1.5 text-sm">
            {guide.sections.map((s) => (
              <li key={s.heading}>
                <a href={`#${slugify(s.heading)}`} className="text-primary hover:underline">
                  {s.heading}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      ) : null}

      <div className="mt-10 space-y-8">
        {guide.sections.map((section) => (
          <section key={section.heading} id={slugify(section.heading)} className="scroll-mt-24">
            <h2 className="font-heading text-xl font-bold text-foreground">{section.heading}</h2>
            {section.body.map((p) => (
              <p
                key={p}
                className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base"
              >
                {p}
              </p>
            ))}
            {section.bullets?.length ? (
              <ul className="mt-4 space-y-2">
                {section.bullets.map((b) => (
                  <li key={b} className="flex gap-2 text-sm leading-relaxed text-muted-foreground">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </div>

      {guide.steps?.length ? (
        <section className="mt-12">
          <h2 className="font-heading text-xl font-bold text-foreground">
            Så gör du, steg för steg
          </h2>
          <ol className="mt-4 space-y-3">
            {guide.steps.map((step, i) => (
              <li
                key={step.title}
                className="flex gap-3 rounded-lg border border-border bg-card p-5"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 font-heading text-sm font-bold text-primary">
                  {i + 1}
                </span>
                <span>
                  <span className="block font-heading text-base font-semibold text-foreground">
                    {step.title}
                  </span>
                  <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                    {step.text}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {guide.facts ? (
        <section className="mt-12">
          <h2 className="font-heading text-xl font-bold text-foreground">{guide.facts.heading}</h2>
          <div className="mt-4 overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th scope="col" className="px-4 py-2.5 text-left font-heading font-semibold">
                    {guide.facts.columns[0]}
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-left font-heading font-semibold">
                    {guide.facts.columns[1]}
                  </th>
                </tr>
              </thead>
              <tbody>
                {guide.facts.rows.map(([a, b]) => (
                  <tr key={a} className="border-t border-border">
                    <td className="px-4 py-2.5 text-foreground">{a}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {guide.facts.note ? (
            <p className="mt-2 text-xs text-muted-foreground">{guide.facts.note}</p>
          ) : null}
        </section>
      ) : null}

      <section className="mt-12">
        <h2 className="font-heading text-xl font-bold text-foreground">Vanliga frågor</h2>
        <dl className="mt-4 space-y-4">
          {guide.faq.map((item) => (
            <div key={item.q} className="rounded-lg border border-border bg-card p-5">
              <dt className="font-heading text-base font-semibold text-foreground">{item.q}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="mt-12 rounded-lg border border-border bg-card p-6 text-center">
        <p className="font-heading text-lg font-bold text-foreground">Redo att sälja?</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Fota prylen så skriver vi annonsen och föreslår pris åt dig.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Button asChild>
            <Link to="/skapa">Skapa annons</Link>
          </Button>
          {guide.category ? (
            <Button asChild variant="outline">
              <Link to="/salja/$kategori" params={{ kategori: guide.category }}>
                {categoryLabel(guide.category)} till salu
              </Link>
            </Button>
          ) : (
            <Button asChild variant="outline">
              <Link to="/annonser">Se alla annonser</Link>
            </Button>
          )}
        </div>
      </div>

      <section className="mt-12">
        <h2 className="font-heading text-base font-bold text-foreground">Fler guider</h2>
        <ul className="mt-3 space-y-2">
          {others.map((g) => (
            <li key={g.slug}>
              <Link
                to="/guider/$slug"
                params={{ slug: g.slug }}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                {g.title}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}
